var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import Avatar from '@mui/material/Avatar';
import { styled } from '@mui/material/styles';
import CustomIcon from '../Icon';
import { logErrorIfInvalidProp } from '../utils/errorHandling';
const validSizes = ['small', 'medium', 'large', 'xlarge', 'xxlarge'];
const validTypes = [
    'staticNeutral',
    'staticSubtle',
    'staticEmphasis',
    'interactiveNeutral',
    'interactiveSubtle',
    'interactiveEmphasis'
];
// Function to get avatar size based on the size prop
const getAvatarSize = (theme, bdsSize) => {
    if (bdsSize && !validSizes.includes(bdsSize)) {
        logErrorIfInvalidProp('Avatar', 'bdsSize', bdsSize);
        return undefined;
    }
    const size = theme.palette.avatar.size[bdsSize];
    return {
        width: size.width,
        height: size.height,
        fontSize: size.font.size,
        lineHeight: size.font.lineHeight
    };
};
// Function to get styles based on the bdsType, disabled, and selected props
const getAvatarStyles = (theme, bdsType, selected) => {
    if (bdsType && !validTypes.includes(bdsType)) {
        logErrorIfInvalidProp('Avatar', 'bdsType', bdsType);
        return undefined;
    }
    const defaultBorderWidth = theme.palette.avatar.border.width;
    const stylesMap = {
        staticNeutral: {
            backgroundColor: theme.palette.avatar.static.neutral.background,
            border: `${defaultBorderWidth} solid ${theme.palette.avatar.static.neutral.border}`,
            color: theme.palette.avatar.static.neutral.text
        },
        staticSubtle: {
            backgroundColor: theme.palette.avatar.static.subtle.background,
            border: `${defaultBorderWidth} solid ${theme.palette.avatar.static.subtle.border}`,
            color: theme.palette.avatar.static.subtle.text
        },
        staticEmphasis: {
            backgroundColor: theme.palette.avatar.static.emphasis.background,
            border: `${defaultBorderWidth} solid ${theme.palette.avatar.static.emphasis.border}`,
            color: theme.palette.avatar.static.emphasis.text
        },
        interactiveNeutral: {
            cursor: 'pointer',
            backgroundColor: selected
                ? theme.palette.avatar.interactive.neutral.background.selected.default
                : theme.palette.avatar.interactive.neutral.background.default,
            border: selected
                ? `${defaultBorderWidth} solid ${theme.palette.avatar.interactive.neutral.border.selected.default}`
                : `${defaultBorderWidth} solid ${theme.palette.avatar.interactive.neutral.border.default}`,
            color: theme.palette.avatar.interactive.neutral.text.default,
            hover: {
                backgroundColor: selected
                    ? theme.palette.avatar.interactive.neutral.background.selected.hover
                    : theme.palette.avatar.interactive.neutral.background.hover,
                border: selected
                    ? `${defaultBorderWidth} solid ${theme.palette.avatar.interactive.neutral.border.selected.hover}`
                    : `${defaultBorderWidth} solid ${theme.palette.avatar.interactive.neutral.border.hover}`
            },
            focus: {
                backgroundColor: theme.palette.avatar.interactive.neutral.background.focus,
                border: `${defaultBorderWidth} solid ${theme.palette.avatar.interactive.neutral.border.focus}`
            }
        },
        interactiveSubtle: {
            cursor: 'pointer',
            backgroundColor: selected
                ? theme.palette.avatar.interactive.subtle.background.selected.default
                : theme.palette.avatar.interactive.subtle.background.default,
            border: selected
                ? `${defaultBorderWidth} solid ${theme.palette.avatar.interactive.subtle.border.selected.default}`
                : `${defaultBorderWidth} solid ${theme.palette.avatar.interactive.subtle.border.default}`,
            color: theme.palette.avatar.interactive.subtle.text.default,
            hover: {
                backgroundColor: selected
                    ? theme.palette.avatar.interactive.subtle.background.selected.hover
                    : theme.palette.avatar.interactive.subtle.background.default,
                border: selected
                    ? `${defaultBorderWidth} solid ${theme.palette.avatar.interactive.subtle.border.selected.hover}`
                    : `${defaultBorderWidth} solid ${theme.palette.avatar.interactive.subtle.border.hover}`
            },
            focus: {
                backgroundColor: theme.palette.avatar.interactive.subtle.background.focus,
                border: `${defaultBorderWidth} solid ${theme.palette.avatar.interactive.subtle.border.focus}`
            }
        },
        interactiveEmphasis: {
            cursor: 'pointer',
            backgroundColor: selected
                ? theme.palette.avatar.interactive.emphasis.background.selected.default
                : theme.palette.avatar.interactive.emphasis.background.default,
            border: selected
                ? `${defaultBorderWidth} solid ${theme.palette.avatar.interactive.emphasis.border.selected.default}`
                : `${defaultBorderWidth} solid ${theme.palette.avatar.interactive.emphasis.border.default}`,
            color: theme.palette.avatar.interactive.emphasis.text.default,
            hover: {
                backgroundColor: selected
                    ? theme.palette.avatar.interactive.emphasis.background.selected.hover
                    : theme.palette.avatar.interactive.emphasis.background.default,
                border: selected
                    ? `${defaultBorderWidth} solid ${theme.palette.avatar.interactive.emphasis.border.selected.hover}`
                    : `${defaultBorderWidth} solid ${theme.palette.avatar.interactive.emphasis.border.hover}`
            },
            focus: {
                backgroundColor: theme.palette.avatar.interactive.emphasis.background.focus,
                border: `${defaultBorderWidth} solid ${theme.palette.avatar.interactive.emphasis.border.focus}`
            }
        }
    };
    return stylesMap[bdsType] || undefined;
};
const StyledAvatar = styled(Avatar, {
    shouldForwardProp: (prop) => !['bdsType', 'bdsSize'].includes(prop)
})(({ theme, bdsType, bdsSize, selected, disabled }) => {
    var _a, _b, _c, _d;
    const avatarSize = (bdsSize && getAvatarSize(theme, bdsSize)) || undefined;
    const avatarStyles = (bdsType && getAvatarStyles(theme, bdsType, selected)) || undefined;
    return Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, avatarStyles), avatarSize), { fontFamily: theme.palette.avatar.font.family, fontWeight: theme.palette.avatar.font.weight, letterSpacing: theme.palette.avatar.font.letterSpacing, textTransform: theme.palette.avatar.font.textTransform, borderRadius: theme.palette.avatar.border.radius, textCase: 'uppercase', textDecoration: 'none', '&:hover': {
            background: (_a = avatarStyles === null || avatarStyles === void 0 ? void 0 : avatarStyles.hover) === null || _a === void 0 ? void 0 : _a.backgroundColor,
            border: (_b = avatarStyles === null || avatarStyles === void 0 ? void 0 : avatarStyles.hover) === null || _b === void 0 ? void 0 : _b.border
        }, '&:focus-visible': {
            background: (_c = avatarStyles === null || avatarStyles === void 0 ? void 0 : avatarStyles.focus) === null || _c === void 0 ? void 0 : _c.backgroundColor,
            border: (_d = avatarStyles === null || avatarStyles === void 0 ? void 0 : avatarStyles.focus) === null || _d === void 0 ? void 0 : _d.border
        }, '&.bds-chip-avatar': Object.assign({}, avatarSize) }), (disabled && {
        cursor: 'default',
        backgroundColor: theme.palette.avatar.interactive.emphasis.background.disabled,
        border: `${theme.palette.avatar.border.width} solid ${theme.palette.avatar.interactive.emphasis.border.disabled}`,
        color: theme.palette.avatar.interactive.emphasis.text.disabled,
        opacity: 1,
        '&:hover': {},
        '&:focus-visible': {}
    })), { 
        // If the avatar is used in a disabled chip, apply the disabled styles
        '.MuiChip-root.Mui-disabled &': {
            backgroundColor: theme.palette.avatar.interactive.neutral.background.disabled,
            border: `${theme.palette.avatar.border.width} solid ${theme.palette.avatar.interactive.neutral.border.disabled}`,
            color: theme.palette.avatar.interactive.neutral.text.disabled,
            opacity: 1
        } });
});
// Wrapper for the avatar component if used with the Chip component
// Otherwise the Chip Styles will override the Avatar styles
const StyledAvatarWrapper = styled('div')({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
});
const BDSAvatar = props => {
    const { disabled, selected, onClick, children, bdsType, bdsSize } = props, rest = __rest(props
    // Handle click event for the avatar
    , ["disabled", "selected", "onClick", "children", "bdsType", "bdsSize"]);
    // Handle click event for the avatar
    const handleAvatarClick = (event) => {
        event.stopPropagation();
        if (!disabled && onClick) {
            onClick(event);
        }
    };
    // When using the CustomIcon component as a child of BDSAvatar
    // we need to pass the selected prop to the CustomIcon
    // Clone children to pass `selected` and remove `onClick` from CustomIcon
    const clonedChildren = React.Children.map(children, child => {
        if (React.isValidElement(child) && child.type === CustomIcon) {
            return React.cloneElement(child, {
                selected,
                onClick
            });
        }
        return child;
    });
    return (React.createElement(StyledAvatar, Object.assign({ onClick: handleAvatarClick, disabled: disabled, selected: selected, bdsSize: bdsSize, bdsType: bdsType }, rest), clonedChildren));
};
BDSAvatar.displayName = 'Avatar';
export { StyledAvatarWrapper };
export default BDSAvatar;
//# sourceMappingURL=Avatar.js.map